import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { gql, useApolloClient, useMutation } from "@apollo/client";
import {
  ArrowLeft,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  CreditCard,
  Edit,
  Mail,
  MapPin,
  Phone,
  RefreshCw,
  ShoppingCart,
  XCircle,
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  BuyAdminManagedUser,
  BuyOrderFinancial,
  BuyOrderFinancialsSummary,
  buyAdminApi,
} from "../../../services/buyAdminApi";
import BuyAdminThemeToggle from "../shared/BuyAdminThemeToggle";
import "../shared/buyAdminThemeGlobals.css";
import styles from "./UserAdminManagement.module.css";

const SYNC_MEMBER_PROFILE_TO_SELL = gql`
  mutation SyncMemberProfileToSell($where: user_bool_exp!, $input: user_set_input!) {
    update_user(where: $where, _set: $input) {
      affected_rows
    }
  }
`;

const FIND_SELL_MEMBER_BY_EMAIL = gql`
  query FindSellMemberByEmail($email: String!) {
    user(where: { u_email_id: { _ilike: $email } }, limit: 2) {
      u_id
    }
  }
`;

const DUPLICATE_EMAIL_MESSAGE = "This email is already in use. Please use a different email address.";

const formatCurrency = (value: number | null | undefined) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const formatCurrencyWithCents = (value: number | null | undefined) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value: string | null | undefined) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-US");
};

const formatStatus = (value: string | null | undefined) =>
  (value || "-")
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatSeatLocationLine = (order: BuyOrderFinancial) => {
  const parts = [
    order.section ? `Section ${order.section}` : "",
    order.row ? `Row ${order.row}` : "",
    order.seats ? `Seats ${order.seats}` : "",
  ].filter(Boolean);
  const ticketCount = Number(order.ticket_quantity || order.item_count || 0);
  parts.push(`${ticketCount} ${ticketCount === 1 ? "ticket" : "tickets"}`);
  return parts.join(" | ");
};

const getInitials = (member: BuyAdminManagedUser | null) => {
  const name = member?.full_name || member?.email || "Member";
  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("");
  return (initials || name[0] || "M").toUpperCase();
};

const getFulfillmentClass = (status: string | null | undefined) => {
  const normalized = (status || "").toLowerCase();
  if (["fulfilled", "complete", "completed", "success", "succeeded", "paid"].includes(normalized)) {
    return styles.fulfillmentFulfilled;
  }
  if (["pending", "processing", "purchased", "created"].includes(normalized)) {
    return styles.fulfillmentPurchased;
  }
  if (["failed", "cancelled", "canceled", "refunded"].includes(normalized)) {
    return styles.fulfillmentFailed;
  }
  return styles.fulfillmentPending;
};

const firstFiniteNumber = (...values: Array<number | null | undefined>) => {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
  }
  return 0;
};

const formatMemberLocation = (member: BuyAdminManagedUser): string => {
  const cityState = [member.city, member.state].filter(Boolean).join(", ");
  return [cityState, member.zip].filter(Boolean).join(" ") || member.street_address || "Location unavailable";
};

const MemberPersonalInfo = () => {
  const { userId = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as { member?: BuyAdminManagedUser } | null;
  const apolloClient = useApolloClient();
  const [syncMemberProfileToSell] = useMutation(SYNC_MEMBER_PROFILE_TO_SELL);

  const [member, setMember] = useState<BuyAdminManagedUser | null>(locationState?.member || null);
  const [orders, setOrders] = useState<BuyOrderFinancial[]>([]);
  const [metricSummary, setMetricSummary] = useState<BuyOrderFinancialsSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [metricsFiltersOpen, setMetricsFiltersOpen] = useState(false);
  const [purchaseFiltersOpen, setPurchaseFiltersOpen] = useState(false);
  const [metricsFrom, setMetricsFrom] = useState("");
  const [metricsTo, setMetricsTo] = useState("");
  const [purchaseFrom, setPurchaseFrom] = useState("");
  const [purchaseTo, setPurchaseTo] = useState("");
  const [orderStatus, setOrderStatus] = useState("");
  const [orderSearch, setOrderSearch] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);
  const [compEnding, setCompEnding] = useState(false);
  const [infoSaving, setInfoSaving] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    phone: "",
    membershipStatus: "",
  });
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    cardNumber: "",
    expiry: "",
    cvv: "",
    name: "",
  });
  const [savedPayment, setSavedPayment] = useState({
    brand: "",
    last4: "",
    expiry: "",
    name: "",
  });
  const [filterDraft, setFilterDraft] = useState({
    from: "",
    to: "",
    status: "",
    order: "",
  });
  const [metricsFilterDraft, setMetricsFilterDraft] = useState({
    from: "",
    to: "",
  });

  const loadMember = async () => {
    if (!userId) return;
    setLoading(true);
    setErrorMessage("");
    try {
      const response = await buyAdminApi.getUserAdminCustomers({
        limit: 10,
        offset: 0,
        search: userId,
      });
      const exactMember = response.data.find((item) => item.id === userId) || response.data[0] || null;
      setMember(exactMember);
      if (!exactMember) {
        setErrorMessage("Member was not found in the buy backend.");
      }
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to load member personal info.");
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    if (!userId) return;
    setOrdersLoading(true);
    try {
      const response = await buyAdminApi.getOrderFinancials({
        limit: 200,
        offset: 0,
        userId,
        order: orderSearch || undefined,
        orderStatus: orderStatus || undefined,
        from: purchaseFrom || undefined,
        to: purchaseTo || undefined,
        completedOnly: "false",
      });
      setOrders(response.data);
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to load member purchase history.");
    } finally {
      setOrdersLoading(false);
    }
  };

  const loadMetrics = async () => {
    if (!userId) return;
    try {
      const response = await buyAdminApi.getOrderFinancials({
        limit: 1,
        offset: 0,
        userId,
        from: metricsFrom || undefined,
        to: metricsTo || undefined,
        completedOnly: "false",
      });
      setMetricSummary(response.summary);
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to load member metrics.");
    }
  };

  const loadPaymentMethod = async () => {
    if (!userId) return;
    try {
      const response = await buyAdminApi.getBuyAdminUserPaymentMethod(userId);
      if (response.data) {
        setSavedPayment({
          brand: response.data.card_brand || "",
          last4: response.data.card_last4 || "",
          expiry: response.data.card_expiry || "",
          name: response.data.cardholder_name || "",
        });
      } else {
        setSavedPayment({ brand: "", last4: "", expiry: "", name: "" });
      }
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to load payment information.");
    }
  };

  useEffect(() => {
    if (!member) return;
    setEditForm({
      name: member.full_name || member.email || "",
      email: member.email || "",
      phone: member.phone_number || "",
      membershipStatus: member.membership_status || "",
    });
  }, [member]);

  useEffect(() => {
    loadMember();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, purchaseFrom, purchaseTo, orderStatus, orderSearch]);

  useEffect(() => {
    loadMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, metricsFrom, metricsTo]);

  useEffect(() => {
    loadPaymentMethod();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (errorMessage !== DUPLICATE_EMAIL_MESSAGE) return;
    const timer = window.setTimeout(() => setErrorMessage(""), 2000);
    return () => window.clearTimeout(timer);
  }, [errorMessage]);

  const metrics = useMemo(() => {
    const historyItems = metricSummary?.history_item_count;
    const historySpent = metricSummary?.history_total_spent;
    const historySavings = metricSummary?.history_total_savings;
    const historyEvents = metricSummary?.history_event_count;
    const fallbackItems = orders.reduce((sum, order) => sum + Number(order.item_count ?? 0), 0);
    const fallbackSpent = orders.reduce((sum, order) => sum + Number(order.customer_total ?? 0), 0);
    const fallbackSavings = orders.reduce((sum, order) => sum + Number(order.customer_savings ?? 0), 0);
    const firstDate = member?.created_at ? new Date(member.created_at) : null;
    const months = firstDate && !Number.isNaN(firstDate.getTime())
      ? Math.max(1, Math.ceil((Date.now() - firstDate.getTime()) / (1000 * 60 * 60 * 24 * 30)))
      : 1;
    const ticketsBought = firstFiniteNumber(historyItems, fallbackItems, member?.order_count);
    const totalSpent = firstFiniteNumber(historySpent, fallbackSpent, member?.lifetime_spend);
    const totalSavings = firstFiniteNumber(historySavings, fallbackSavings, member?.lifetime_savings);
    const eventsAttended = firstFiniteNumber(historyEvents, ticketsBought);

    return {
      ticketsBought,
      totalSpent,
      totalSavings,
      eventsAttended,
      avgTicketsPerMonth: (ticketsBought / months).toFixed(1),
    };
  }, [member, metricSummary, orders]);

  const latestPayment = orders.find((order) => order.payment_status || order.payment_intent_id);
  const paymentLast4 = savedPayment.last4 || latestPayment?.payment_intent_id?.slice(-4) || "";
  const paymentBrand = savedPayment.brand || (latestPayment?.payment_status ? formatStatus(latestPayment.payment_status) : "");

  const isMemberDeactivated = (user: BuyAdminManagedUser) =>
    Boolean(user.is_deactivated ?? !user.is_active);

  const getMemberStatus = (user: BuyAdminManagedUser) => {
    if (isMemberDeactivated(user)) return "deactivated";
    if (!user.is_verified) return "not_verified";
    return "active";
  };

  const getMemberStatusLabel = (user: BuyAdminManagedUser) => {
    const status = getMemberStatus(user);
    return status === "not_verified" ? "Not Verified" : formatStatus(status);
  };

  const updateMemberStatus = async (isActive: boolean) => {
    if (!member || isActive === !isMemberDeactivated(member)) return;
    setStatusSaving(true);
    try {
      await buyAdminApi.updateBuyAdminUserStatus(member.id, {
        isActive,
        note: "Updated from buy admin member details",
        role: "admin",
      });
      setMember({
        ...member,
        is_active: isActive,
        is_deactivated: !isActive,
        deactivated_at: isActive ? null : new Date().toISOString(),
      });
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to update member status.");
    } finally {
      setStatusSaving(false);
    }
  };

  const endCompMembership = async () => {
    if (!member || compEnding) return;
    const confirmation = await Swal.fire({
      title: "End comp membership?",
      text: `${member.full_name || member.email} will become Not Verified. On their next login they enter a one-time code, then choose and pay for a monthly or annual plan. No free trial is offered.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "End comp membership",
      cancelButtonText: "Keep comp",
      reverseButtons: true,
      focusCancel: true,
    });
    if (!confirmation.isConfirmed) return;
    setCompEnding(true);
    setErrorMessage("");
    try {
      await buyAdminApi.endBuyAdminCompMembership(member.id);
      await loadMember();
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to end the comp membership.");
    } finally {
      setCompEnding(false);
    }
  };

  const saveEditInfo = async () => {
    if (!member) return;
    const parts = editForm.name.trim().split(" ").filter(Boolean);
    const nextProfile = {
      firstName: parts[0] || "",
      lastName: parts.slice(1).join(" "),
      email: editForm.email.trim(),
      phoneNumber: editForm.phone.trim(),
    };
    const previousSellProfile = {
      u_first_name: member.first_name || "",
      u_last_name: member.last_name || "",
      u_phone_number: member.phone_number || "",
    };
    setInfoSaving(true);
    setErrorMessage("");
    try {
      if (!member.sell_user_id) {
        const buyEmailCheck = await buyAdminApi.checkBuyAdminUserEmailAvailability(member.id, nextProfile.email);
        if (!buyEmailCheck.data.available) {
          throw new Error(DUPLICATE_EMAIL_MESSAGE);
        }
        const response = await buyAdminApi.updateBuyAdminUserInfo(member.id, {
          firstName: nextProfile.firstName,
          lastName: nextProfile.lastName,
          email: nextProfile.email,
          phoneNumber: nextProfile.phoneNumber,
          note: "Updated Buy-only member from buy admin details",
          role: "admin",
        });
        setMember(response.data);
        setIsEditing(false);
        return;
      }
      const sellUserId = member.sell_user_id;
      const sellUserWhere = { u_id: { _eq: sellUserId } };

      const [buyEmailCheck, sellEmailCheck] = await Promise.all([
        buyAdminApi.checkBuyAdminUserEmailAvailability(member.id, nextProfile.email),
        apolloClient.query<{ user: Array<{ u_id: string }> }>({
          query: FIND_SELL_MEMBER_BY_EMAIL,
          variables: { email: nextProfile.email },
          fetchPolicy: "no-cache",
        }),
      ]);

      if (!buyEmailCheck.data.available) {
        throw new Error(DUPLICATE_EMAIL_MESSAGE);
      }
      if (sellEmailCheck.data.user.some((user) => user.u_id !== sellUserId)) {
        throw new Error(DUPLICATE_EMAIL_MESSAGE);
      }

      const emailChanged = nextProfile.email.toLowerCase() !== member.email.trim().toLowerCase();
      if (emailChanged) {
        await buyAdminApi.updateUnifiedUserEmail(sellUserId, nextProfile.email);
      }

      const sellResult = await syncMemberProfileToSell({
        variables: {
          where: sellUserWhere,
          input: {
            u_first_name: nextProfile.firstName,
            u_last_name: nextProfile.lastName,
            u_phone_number: nextProfile.phoneNumber,
          },
        },
      });

      if (sellResult.data?.update_user?.affected_rows !== 1) {
        throw new Error(
          sellResult.data?.update_user?.affected_rows === 0
            ? "The linked Sell profile was not found. No profile changes were saved."
            : "More than one Sell profile matched this user. No Buy profile changes were saved.",
        );
      }

      let response;
      try {
        response = await buyAdminApi.updateBuyAdminUserInfo(member.id, {
          firstName: nextProfile.firstName,
          lastName: nextProfile.lastName,
          email: nextProfile.email,
          phoneNumber: nextProfile.phoneNumber,
          note: "Updated from buy admin member details and synchronized to Sell",
          role: "admin",
        });
      } catch (buyError) {
        // Keep the stores aligned if the Buy/Combined write fails after Sell succeeds.
        try {
          await syncMemberProfileToSell({
            variables: {
              where: sellUserWhere,
              input: previousSellProfile,
            },
          });
        } catch (rollbackError) {
          console.error("Failed to roll back Sell profile after Buy profile update failed", rollbackError);
        }
        throw buyError;
      }
      setMember({ ...response.data, email: nextProfile.email });
      setIsEditing(false);
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to save member info.");
    } finally {
      setInfoSaving(false);
    }
  };

  const savePaymentMethod = async () => {
    const digits = paymentForm.cardNumber.replace(/\D/g, "");
    if (!digits || !paymentForm.expiry || !paymentForm.cvv || !paymentForm.name.trim()) {
      setErrorMessage("Please fill in all payment fields.");
      return;
    }
    const brand = digits.startsWith("4") ? "Visa" : digits.startsWith("5") ? "Mastercard" : "Card";
    setPaymentSaving(true);
    setErrorMessage("");
    try {
      const response = await buyAdminApi.updateBuyAdminUserPaymentMethod(userId, {
        cardBrand: brand,
        cardLast4: digits.slice(-4),
        cardExpiry: paymentForm.expiry,
        cardholderName: paymentForm.name.trim(),
        note: "Updated from buy admin member details",
        role: "admin",
      });
      setSavedPayment({
        brand: response.data.card_brand || "",
        last4: response.data.card_last4 || "",
        expiry: response.data.card_expiry || "",
        name: response.data.cardholder_name || "",
      });
      setPaymentForm({ cardNumber: "", expiry: "", cvv: "", name: "" });
      setShowPaymentForm(false);
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to save payment information.");
    } finally {
      setPaymentSaving(false);
    }
  };

  const applyPurchaseFilter = () => {
    setPurchaseFrom(filterDraft.from);
    setPurchaseTo(filterDraft.to);
    setOrderStatus(filterDraft.status);
    setOrderSearch(filterDraft.order);
    setPurchaseFiltersOpen(false);
  };

  const applyMetricsFilter = () => {
    setMetricsFrom(metricsFilterDraft.from);
    setMetricsTo(metricsFilterDraft.to);
    setMetricsFiltersOpen(false);
  };

  const clearMetricsFilter = () => {
    setMetricsFilterDraft({ from: "", to: "" });
    setMetricsFrom("");
    setMetricsTo("");
    setMetricsFiltersOpen(false);
  };

  const clearPurchaseFilter = () => {
    setFilterDraft({ from: "", to: "", status: "", order: "" });
    setPurchaseFrom("");
    setPurchaseTo("");
    setOrderStatus("");
    setOrderSearch("");
    setPurchaseFiltersOpen(false);
  };

  const purchaseFilterLabel = purchaseFrom || purchaseTo
    ? `${purchaseFrom ? `From ${formatDate(purchaseFrom)}` : ""}${purchaseFrom && purchaseTo ? " - " : ""}${purchaseTo ? `To ${formatDate(purchaseTo)}` : ""}`
    : "";
  const metricsFilterLabel = metricsFrom || metricsTo
    ? `${metricsFrom ? `From ${formatDate(metricsFrom)}` : ""}${metricsFrom && metricsTo ? " - " : ""}${metricsTo ? `To ${formatDate(metricsTo)}` : ""}`
    : "";

  return (
    <div className={`${styles.page} buy-admin-theme-scope`} data-testid="buy-member-personal-info-page">
      <div className={styles.memberDetailsWrap}>
        <div className={styles.memberBackRow}>
          <button className={styles.backLinkButton} onClick={() => navigate("/buy-user-admin-management")}>
            <ArrowLeft size={16} />
            Back to Members
          </button>
          <div className={`${styles.headerActions} buy-admin-theme-actions`}>
            <button
              type="button"
              className={styles.refreshButton}
              onClick={() => { loadMember(); loadOrders(); loadMetrics(); loadPaymentMethod(); }}
              disabled={loading || ordersLoading}
              aria-label="Refresh member details"
              title="Refresh"
            >
              <RefreshCw size={16} className={loading || ordersLoading ? styles.spin : undefined} />
            </button>
            <BuyAdminThemeToggle iconOnly />
          </div>
        </div>

        {errorMessage && (
          <div className={`${styles.inlineAlert} ${styles.inlineError}`}>
            <XCircle size={18} />
            {errorMessage}
          </div>
        )}

        {loading && !member ? (
          <div className={styles.detailSectionCard}>
            <div className={styles.emptyCell}>Loading member...</div>
          </div>
        ) : member ? (
          <>
            <section className={styles.memberSummaryCard}>
              <div className={styles.memberAvatar}>{getInitials(member)}</div>
              <div className={styles.memberIdentity}>
                <div className={styles.memberNameRow}>
                  {isEditing ? (
                    <input
                      className={styles.editNameInput}
                      value={editForm.name}
                      onChange={(event) => setEditForm({ ...editForm, name: event.target.value })}
                    />
                  ) : (
                    <h1>{member.full_name || member.email}</h1>
                  )}
                  <div className={styles.statusMenuWrap}>
                    <button
                      className={`${styles.memberStatusPill} ${
                        getMemberStatus(member) === "not_verified"
                          ? styles.memberStatusNotVerified
                          : !isMemberDeactivated(member)
                            ? styles.memberStatusActive
                            : styles.memberStatusInactive
                      }`}
                      disabled={statusSaving}
                      type="button"
                    >
                      {getMemberStatusLabel(member)} <ChevronDown size={13} />
                    </button>
                    <div className={styles.statusMenu}>
                      <button type="button" onClick={() => updateMemberStatus(true)}>
                        <span className={`${styles.statusBadge} ${styles.status_active}`}>Active</span>
                        {!isMemberDeactivated(member) && <CheckCircle2 size={15} />}
                      </button>
                      <button type="button" onClick={() => updateMemberStatus(false)}>
                        <span className={`${styles.statusBadge} ${styles.status_inactive}`}>Deactivated</span>
                        {isMemberDeactivated(member) && <CheckCircle2 size={15} />}
                      </button>
                    </div>
                  </div>
                  {member.has_live_admin_grant && (
                    <button
                      type="button"
                      className={styles.secondaryButton}
                      disabled={compEnding}
                      onClick={endCompMembership}
                    >
                      {compEnding ? "Ending comp…" : "End Comp Membership"}
                    </button>
                  )}
                </div>
                {isEditing ? (
                  <div className={styles.editInfoGrid}>
                    <label>Email<input value={editForm.email} onChange={(event) => setEditForm({ ...editForm, email: event.target.value })} /></label>
                    <label>Phone<input value={editForm.phone} onChange={(event) => setEditForm({ ...editForm, phone: event.target.value })} /></label>
                    <label>Membership<input value={editForm.membershipStatus} disabled /></label>
                  </div>
                ) : (
                  <div className={styles.memberContactGrid}>
                    <span><Mail size={16} className={styles.emailIcon} />{member.email || "-"}</span>
                    <span><Phone size={16} className={styles.phoneIcon} />{member.phone_number || "-"}</span>
                    <span title={[member.street_address, formatMemberLocation(member)].filter(Boolean).join(", ")}>
                      <MapPin size={16} className={styles.locationIcon} />{formatMemberLocation(member)}
                    </span>
                  </div>
                )}
              </div>
              <div className={styles.memberSinceBox}>
                <span>Member since</span>
                <strong>{formatDate(member.created_at)}</strong>
                {isEditing ? (
                  <div className={styles.editActionRow}>
                    <button className={styles.saveSmallButton} type="button" onClick={saveEditInfo} disabled={infoSaving}>
                      {infoSaving ? "Saving..." : "Save"}
                    </button>
                    <button className={styles.compactOutlineButton} type="button" onClick={() => setIsEditing(false)} disabled={infoSaving}>Cancel</button>
                  </div>
                ) : (
                  <button className={styles.compactOutlineButton} type="button" onClick={() => setIsEditing(true)}>
                    <Edit size={15} />
                    Edit Info
                  </button>
                )}
              </div>
            </section>

            <section className={styles.detailSectionCard}>
              <div className={styles.sectionTitleRow}>
                <h2><CreditCard size={20} className={styles.paymentTitleIcon} />Payment Information</h2>
                {/* <button className={styles.compactOutlineButton} type="button" onClick={() => setShowPaymentForm(!showPaymentForm)}>
                  {showPaymentForm ? "Cancel" : "Update Payment Method"}
                </button> */}
              </div>
              {showPaymentForm ? (
                <div className={styles.paymentFormGrid}>
                  <label className={styles.paymentFullField}>Card Number<input placeholder="1234 5678 9012 3456" value={paymentForm.cardNumber} onChange={(event) => setPaymentForm({ ...paymentForm, cardNumber: event.target.value.replace(/[^\d ]/g, "").slice(0, 19) })} /></label>
                  <label>Expiry Date<input placeholder="MM/YY" value={paymentForm.expiry} onChange={(event) => setPaymentForm({ ...paymentForm, expiry: event.target.value.slice(0, 5) })} /></label>
                  <label>CVV<input type="password" placeholder="123" value={paymentForm.cvv} onChange={(event) => setPaymentForm({ ...paymentForm, cvv: event.target.value.replace(/\D/g, "").slice(0, 4) })} /></label>
                  <label className={styles.paymentFullField}>Cardholder Name<input placeholder="Name on card" value={paymentForm.name} onChange={(event) => setPaymentForm({ ...paymentForm, name: event.target.value })} /></label>
                  <button className={styles.paymentSaveButton} type="button" onClick={savePaymentMethod} disabled={paymentSaving}>
                    {paymentSaving ? "Saving..." : "Save Payment Method"}
                  </button>
                </div>
              ) : (
                <div className={styles.paymentDisplay}>
                  <div className={styles.paymentIconBox}><CreditCard size={32} /></div>
                  <div>
                    <strong>{paymentBrand ? `${paymentBrand} ${paymentLast4 ? `.... ${paymentLast4}` : ""}` : "Payment method unavailable"}</strong>
                    <span>{savedPayment.expiry ? `Expires ${savedPayment.expiry}` : latestPayment?.payment_intent_id ? `Intent ${latestPayment.payment_intent_id}` : "No saved card details returned by buy admin API"}</span>
                    {savedPayment.name && <span>{savedPayment.name}</span>}
                  </div>
                </div>
              )}
            </section>

            <section className={styles.detailSectionCard}>
              <div className={styles.sectionTitleRow}>
                <h2><BarChart3 size={20} className={styles.metricsTitleIcon} />User Metrics</h2>
                <div className={styles.filterMenuWrap}>
                  <button className={styles.filterToggle} type="button" onClick={() => {
                    setMetricsFilterDraft({ from: metricsFrom, to: metricsTo });
                    setMetricsFiltersOpen(!metricsFiltersOpen);
                  }}>
                    <CalendarDays size={15} />
                    Filter
                  </button>
                  {metricsFiltersOpen && (
                    <div className={styles.filterPopover}>
                      <div className={styles.filterPopoverHeader}>
                        <strong>Filter Metrics</strong>
                        <span>{metricsFilterDraft.from ? `From ${formatDate(metricsFilterDraft.from)}` : "Select date range"}</span>
                      </div>
                      <div className={styles.filterPopoverBody}>
                        <label>From<input type="date" value={metricsFilterDraft.from} onChange={(event) => setMetricsFilterDraft({ ...metricsFilterDraft, from: event.target.value })} /></label>
                        <label>To<input type="date" value={metricsFilterDraft.to} onChange={(event) => setMetricsFilterDraft({ ...metricsFilterDraft, to: event.target.value })} /></label>
                      </div>
                      <div className={styles.filterPopoverFooter}>
                        <button type="button" onClick={clearMetricsFilter}>Clear</button>
                        <button type="button" onClick={applyMetricsFilter}>Apply</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {metricsFilterLabel && (
                <div className={styles.activeFilterChips}>
                  <span><CalendarDays size={14} />{metricsFilterLabel}<button type="button" onClick={clearMetricsFilter}>x</button></span>
                </div>
              )}
              <div className={styles.memberMetricGrid}>
                <div className={`${styles.memberMetricTile} ${styles.metricBlue}`}><span>Tickets Bought</span><strong>{metrics.ticketsBought}</strong></div>
                <div className={`${styles.memberMetricTile} ${styles.metricSlate}`}><span>Total Spent</span><strong>{formatCurrency(metrics.totalSpent)}</strong></div>
                <div className={`${styles.memberMetricTile} ${styles.metricGreen}`}><span>Total Savings</span><strong>{formatCurrencyWithCents(metrics.totalSavings)}</strong></div>
                <div className={`${styles.memberMetricTile} ${styles.metricPurple}`}><span>Events Attended</span><strong>{metrics.eventsAttended}</strong></div>
                <div className={`${styles.memberMetricTile} ${styles.metricAmber}`}><span>Avg Tickets/Month</span><strong>{metrics.avgTicketsPerMonth}</strong></div>
              </div>
            </section>

            <section className={styles.detailSectionCard}>
              <div className={styles.sectionTitleRow}>
                <h2><ShoppingCart size={20} className={styles.purchaseTitleIcon} />Purchase History</h2>
                <div className={styles.filterMenuWrap}>
                  <button className={styles.filterToggle} type="button" onClick={() => {
                    setFilterDraft({ from: purchaseFrom, to: purchaseTo, status: orderStatus, order: orderSearch });
                    setPurchaseFiltersOpen(!purchaseFiltersOpen);
                  }}>
                    <CalendarDays size={15} />
                    Filter
                  </button>
                  {purchaseFiltersOpen && (
                    <div className={styles.filterPopover}>
                      <div className={styles.filterPopoverHeader}>
                        <strong>Filter Purchases</strong>
                        <span>{filterDraft.from ? `From ${formatDate(filterDraft.from)}` : "Select date range"}</span>
                      </div>
                      <div className={styles.filterPopoverBody}>
                        <label>From<input type="date" value={filterDraft.from} onChange={(event) => setFilterDraft({ ...filterDraft, from: event.target.value })} /></label>
                        <label>To<input type="date" value={filterDraft.to} onChange={(event) => setFilterDraft({ ...filterDraft, to: event.target.value })} /></label>
                        <label>Status
                          <select value={filterDraft.status} onChange={(event) => setFilterDraft({ ...filterDraft, status: event.target.value })}>
                            <option value="">All</option>
                            <option value="completed">Completed</option>
                            <option value="pending">Pending</option>
                            <option value="failed">Failed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </label>
                        <label>Order<input value={filterDraft.order} onChange={(event) => setFilterDraft({ ...filterDraft, order: event.target.value })} placeholder="Search order" /></label>
                      </div>
                      <div className={styles.filterPopoverFooter}>
                        <button type="button" onClick={clearPurchaseFilter}>Clear</button>
                        <button type="button" onClick={applyPurchaseFilter}>Apply</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {(purchaseFilterLabel || orderStatus || orderSearch) && (
                <div className={styles.activeFilterChips}>
                  {purchaseFilterLabel && <span><CalendarDays size={14} />{purchaseFilterLabel}<button type="button" onClick={clearPurchaseFilter}>x</button></span>}
                  {orderStatus && <span>{formatStatus(orderStatus)}<button type="button" onClick={() => { setOrderStatus(""); setFilterDraft({ ...filterDraft, status: "" }); }}>x</button></span>}
                  {orderSearch && <span>{orderSearch}<button type="button" onClick={() => { setOrderSearch(""); setFilterDraft({ ...filterDraft, order: "" }); loadOrders(); }}>x</button></span>}
                </div>
              )}
              <div className={styles.purchaseTableWrap}>
                <table className={styles.purchaseTable}>
                  <thead>
                    <tr>
                      <th>EVENT</th>
                      <th>ORDER</th>
                      <th>STATUS</th>
                      <th>EVENT DATE</th>
                      <th>IN-HAND DATE</th>
                      <th>VENUE / SEATS</th>
                      <th className={styles.right}>PURCHASE PRICE</th>
                      <th className={styles.right}>COMPETITOR PRICE</th>
                      <th className={styles.right}>TOTAL SAVINGS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => {
                      const fulfillment = order.seller_status || order.order_status || order.payment_status || "pending";
                      const savings = Number(order.customer_savings || 0);
                      const competitorPrice = Number(order.customer_total || 0) + savings;
                      return (
                        <tr key={order.order_id}>
                          <td>
                            <strong>{order.event_name || "Unknown Event"}</strong>
                            <span>Ordered {formatDate(order.created_at)}</span>
                          </td>
                          <td>
                            <strong>{order.order_number || `#${order.order_id}`}</strong>
                            {order.ev_order_id && <span>TE {order.ev_order_id}</span>}
                          </td>
                          <td>
                            <span className={`${styles.fulfillmentBadge} ${getFulfillmentClass(fulfillment)}`}>
                              {formatStatus(fulfillment)} <ChevronDown size={13} />
                            </span>
                          </td>
                          <td>
                            <strong>{formatDate(order.event_date || order.created_at)}</strong>
                            {order.event_time && <span>{order.event_time}</span>}
                          </td>
                          <td>{formatDate(order.tickets_in_hand_on)}</td>
                          <td>
                            <strong>{order.venue_name || "-"}</strong>
                            <span>{formatSeatLocationLine(order)}</span>
                          </td>
                          <td className={styles.right}>{formatCurrencyWithCents(order.customer_total)}</td>
                          <td className={styles.right}>{formatCurrencyWithCents(competitorPrice)}</td>
                          <td className={`${styles.right} ${styles.savingsText}`}>{formatCurrencyWithCents(savings)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {!ordersLoading && orders.length === 0 && (
                  <div className={styles.emptyCell}>No purchases found for the selected filters.</div>
                )}
                {ordersLoading && (
                  <div className={styles.emptyCell}>Loading purchases...</div>
                )}
              </div>
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default MemberPersonalInfo;
